'use client';

import { useCallback, useEffect, useState } from 'react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  duration?: number;
}

export type ToastListener = (toast: Toast) => void;

let toastCounter = 0;
const listeners = new Set<ToastListener>();

export function showToast(toast: Omit<Toast, 'id'>): string {
  const id = `toast-${++toastCounter}`;
  const fullToast: Toast = {
    id,
    variant: toast.variant,
    title: toast.title,
    description: toast.description,
    duration: toast.duration ?? 5000,
  };
  listeners.forEach((l) => l(fullToast));
  return id;
}

if (typeof window !== 'undefined' && process.env['NODE_ENV'] !== 'production') {
  (window as unknown as { __kontoraShowToast?: typeof showToast }).__kontoraShowToast = showToast;
}

export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const subscribe = useCallback(
    (toast: Toast) => {
      setToasts((prev) => [...prev, toast]);
      if (toast.duration && toast.duration > 0) {
        setTimeout(() => dismiss(toast.id), toast.duration);
      }
    },
    [dismiss],
  );

  useEffect(() => {
    listeners.add(subscribe);
    return () => {
      listeners.delete(subscribe);
    };
  }, [subscribe]);

  return { toasts, dismiss };
}